// "What was wrong" tab: findings from reading/running the original code, each with a one-click demo.
import type { ToneId } from "../audio/source";
import type { Mode, Values } from "../params";

export interface Finding {
  title: string;
  body: string;
  fixed: string;
  try?: { label: string; mode?: Mode; tone?: ToneId; toneLen?: number; set?: Values; monoSum?: boolean };
}

export const FINDINGS: Finding[] = [
  {
    title: "Samples per revolution are rounded down",
    body: "<code>indexIncr = int(44100·60/rpm/5880)</code>. At 45 rpm that's exactly 10, but at 33.3 rpm it's 13.5 → 13. Each turn then holds 3.8% less audio than it takes to play, so the record plays slow and flat.",
    fixed: "Improved ties angle to time continuously (θ = ω·t), with no rounding.",
    try: { label: "1 kHz tone at 33.3 rpm", mode: "spiral", tone: "sine1k", toneLen: 10, set: { rpm: 33.3 } },
  },
  {
    title: "No anti-alias filter",
    body: "The sketch keeps every 10th sample (an effective 4.4 kHz sample rate) without low-passing first. Anything above 2.2 kHz folds back into the audio as inharmonic noise. On the sweep you can see the red curve stay high below 2.2 kHz the whole time the sweep is above it.",
    fixed: "A windowed-sinc low-pass at each point's own Nyquist frequency, applied while resampling.",
    try: { label: "Sweep 100 Hz–10 kHz", mode: "spiral", tone: "sweep", toneLen: 10, set: { rpm: 45 } },
  },
  {
    title: "Inner grooves silently lose samples",
    body: "Every turn gets 5880 points, but points closer than <code>minDist</code> (6 px) are dropped. Below r ≈ 4.7″ the points are closer than that, so up to half of them disappear and the inner grooves lose resolution.",
    fixed: "Points are spaced by arc length (≈ minDist everywhere), and the filter cutoff follows the local density.",
    try: { label: "2-minute arpeggio", mode: "spiral", tone: "chord", toneLen: 120, set: { rpm: 45 } },
  },
  {
    title: "Only the left channel is used",
    body: "<code>wavtotext.py</code> reads bytes 0–1 of every 4-byte stereo frame, so the right channel is thrown away. With different content on L and R, half the music is gone.",
    fixed: "Improved mixes to mono by default (or pick L / R / side).",
    try: { label: "L 440 Hz / R 660 Hz", mode: "spiral", tone: "stereoLR", toneLen: 6 },
  },
  {
    title: "No RIAA pre-emphasis",
    body: "Vinyl is cut with the RIAA curve (bass cut, treble boost), and a phono preamp undoes it. A magnetic cartridge also reads velocity, not position. Without pre-emphasis the original sounds muffled and bassy on a normal hi-fi. It's fine on ceramic-cartridge toy players, which read displacement.",
    fixed: "Pick a target player. For magnetic, the audio is RIAA pre-emphasised and integrated before cutting.",
    try: { label: "Magnetic cartridge + chord", mode: "spiral", tone: "chord", toneLen: 8, set: { cartridge: "magnetic" } },
  },
  {
    title: "Centre fixed at 6″",
    body: "Coordinates are <code>6*scaleNum + r·cos θ</code>, so changing <code>diameter</code> doesn't move the record. A 7″ record ends up at (6″, 6″) with an odd margin.",
    fixed: "Centre = margin + diameter/2.",
    try: { label: "7″ record, overlay view", mode: "spiral", tone: "chord", toneLen: 6, set: { diameter: 7, outerRad: 3.3, innerRad: 1.8 } },
  },
  {
    title: "Small export and loop glitches",
    body: "The bounding box is only drawn on intermediate files, not the last one. The audio loop only draws full revolutions (<code>index &lt; length − thetaIter·indexIncr</code>), so up to one turn (1.3 s at 45 rpm) at the end of the song is dropped. The trailing comma in the .txt becomes a NaN sample at the end of the data.",
    fixed: "Every file gets the box, the final partial revolution is kept, and samples are parsed cleanly.",
  },
  {
    title: "Linear strip: amplitude applied twice",
    body: "<code>LinearRecord.pde</code> normalizes the audio to ±amplitude (in px), then multiplies by amplitude again after converting px → pt. So 5 px comes out as 25 px. <code>cutterHeight/2</code> is integer division, which puts the centre line at 2″ on a 5″ strip.",
    fixed: "Single, correct scaling. Rows are centred, and extra audio wraps onto more rows instead of being dropped.",
    try: { label: "Strip mode", mode: "linear", tone: "chord", toneLen: 10 },
  },
  {
    title: "3D: hill-and-dale needs L−R",
    body: "Up-and-down groove motion is read by a stereo cartridge as L and R in opposite phase. On a mono player (or with a mono switch) the two cancel and the record is nearly silent. The original instructions don't mention this.",
    fixed: "The 3D needle outputs L/R in anti-phase. Tick “Sum to mono” to hear the cancellation.",
    try: { label: "3D record, mono sum", mode: "print3d", tone: "chord", toneLen: 5, monoSum: true },
  },
  {
    title: "3D: every 4th sample, no filter, no inner limit",
    body: "<code>rateDivisor = 4</code> keeps every 4th sample (11 kHz) without filtering. The <code>radius &gt; innerRad</code> check is commented out, so a long song spirals straight into the centre hole.",
    fixed: "Band-limited resampling, and grooves stop at innerRad with a warning about what didn't fit.",
    try: { label: "3D sweep", mode: "print3d", tone: "sweep", toneLen: 8 },
  },
  {
    title: "3D: enormous STL in inches",
    body: "Four vertices per sample: 5 s of audio is already 628,566 triangles (31 MB), and a whole song would be gigabytes. The STL is written in inches, but slicers read mm, so it imports 25.4× too small. It also needs unlekker Modelbuilder, a library from the Processing 2 era (the baseline uses the 2012 jar).",
    fixed: "Point density comes from the printer's resolution, the size is estimated before export, and the STL is in mm.",
    try: { label: "3D, 5 s", mode: "print3d", tone: "chord", toneLen: 5 },
  },
  {
    title: "Python 2 only",
    body: "<code>map(ord, list(frame))</code> followed by indexing fails on Python 3 (map returns an iterator). The script also only understands 16-bit stereo, maps −32768 to 0, and builds the output string in O(n²).",
    fixed: "<code>baseline/wavtotext3.py</code> (identical output, any bit depth, <code>--mono</code>). The app decodes any audio format in the browser.",
  },
];

export function renderLearn(root: HTMLElement, apply: (f: Finding) => void) {
  root.innerHTML = `
    <h3>What was wrong (and what to listen for)</h3>
    <p class="muted small">From running the original sketches (Processing 4.5.2, baseline/), porting them bit-for-bit
    to TypeScript (vertex counts match exactly), and comparing against fixed versions with the virtual needle.</p>
    <ol></ol>`;
  const ol = root.querySelector("ol")!;
  for (const f of FINDINGS) {
    const li = document.createElement("li");
    li.innerHTML = `<b>${f.title}.</b> ${f.body}<span class="try">✓ ${f.fixed}</span>`;
    if (f.try) {
      const b = document.createElement("button");
      b.textContent = `Try it: ${f.try.label}`;
      b.style.marginTop = "5px";
      b.onclick = () => apply(f);
      li.appendChild(document.createElement("br"));
      li.appendChild(b);
    }
    ol.appendChild(li);
  }
}
